import {
  AnalyticsAudienceFilterLayer,
  AnalyticsDateShortcut,
  AnalyticsSavedFilterDocument,
  AnalyticsJourneyFilterLayer,
  catalogueFromIds,
} from "../../types/analytics/AnalyticsSavedFilter";
import AnalyticsFilterInputs from "../../types/analytics/AnalyticsFilterInputs";
import { GroupedJourneys } from "../../types/analytics/FilterAvailableValues";
import { normalisePointInTimeDateForDocument } from "./pointInTimeNormaliser";

export function buildSavedFilterDocument(args: {
  selected: AnalyticsFilterInputs;
  journeys: GroupedJourneys[];
  includeJourneyLayer: boolean;
  pageKey: string;
  dateShortcut?: AnalyticsDateShortcut;
  locationLabels?: Map<number, string>;
  jobTitleLabels?: Map<number, string>;
  appraisalLevelLabels?: Map<number, string>;
  userGroupLabels?: Map<number, string>;
}): AnalyticsSavedFilterDocument {
  const journeyLookup = new Map<string, string>();
  args.journeys.forEach((g) =>
    g.journeys.forEach((j) => journeyLookup.set(j.key, j.title))
  );

  const audience: AnalyticsAudienceFilterLayer = {
    activeUsersOnly: args.selected.activeUsersOnly,
    includePartiallyApproved: args.selected.includePartiallyApproved,
    locations: catalogueFromIds(
      args.selected.locationIds,
      args.locationLabels ?? new Map()
    ),
    jobTitles: catalogueFromIds(
      args.selected.jobTitleIds,
      args.jobTitleLabels ?? new Map()
    ),
    appraisalLevels: catalogueFromIds(
      args.selected.appraisalLevelIds,
      args.appraisalLevelLabels ?? new Map()
    ),
    userGroups: catalogueFromIds(
      args.selected.userGroupIds,
      args.userGroupLabels ?? new Map()
    ),
  };

  let journey: AnalyticsJourneyFilterLayer | null = null;
  if (args.includeJourneyLayer) {
    const keys = args.selected.journeyKeys ?? [];
    journey = {
      allJourneys: args.selected.allJourneys,
      journeys: keys.map((key) => ({
        key,
        label: journeyLookup.get(key) ?? key,
      })),
      dateRange:
        args.selected.dateRange?.startDate && args.selected.dateRange?.endDate
          ? {
              startDate: args.selected.dateRange.startDate,
              endDate: args.selected.dateRange.endDate,
            }
          : null,
      dateShortcut: args.dateShortcut ?? "CUSTOM",
    };
  }

  return {
    schemaVersion: 1,
    sourcePageKey: journey ? args.pageKey : null,
    journey,
    audience,
    comparisonProperty: args.selected.comparisonProperty,
    pointInTimeDate: normalisePointInTimeDateForDocument(
      args.selected.pointInTimeDate
    ),
  };
}

/** Whether the current selection would save as page-bound (has journey choices). */
export function selectionHasJourneyLayer(
  selected: AnalyticsFilterInputs
): boolean {
  return (
    selected.allJourneys ||
    (selected.journeyKeys != null && selected.journeyKeys.length > 0)
  );
}
